/**
 * Список записей журнала операций с пояснением каждой строки (§15, §Э1.4).
 */

import { formatCrystalsSigned, formatDateTime, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { LedgerEntryView } from "../api/wallet.js";
import { ledgerLineView } from "../game/ledger-format.js";
import { Crystals } from "./CrystalIcon.jsx";

export interface LedgerListProps {
  readonly locale: LocaleCode;
  readonly timeZone: string;
  readonly entries: readonly LedgerEntryView[];
  readonly loading?: boolean;
  readonly onLoadMore?: () => void;
  readonly hasMore?: boolean;
  readonly onReportEntry?: (entryId: number) => void;
}

export function LedgerList({
  locale,
  timeZone,
  entries,
  loading = false,
  onLoadMore,
  hasMore = false,
  onReportEntry,
}: LedgerListProps): React.JSX.Element {
  const { t } = useTranslation(["economy", "support"]);

  if (entries.length === 0 && !loading) {
    return <p className="screen__empty">{t("ledger.empty")}</p>;
  }

  return (
    <section className="ledger" aria-label={t("ledger.title")}>
      <h2 className="screen__section-title">{t("ledger.title")}</h2>
      <ol className="ledger__list">
        {entries.map((entry) => {
          const line = ledgerLineView(entry);
          const kind = line.currency === "cash" ? "cash" : "hive";
          return (
            <li key={entry.id} className="ledger__row">
              <div className="ledger__main">
                <span className="ledger__op">{t(line.i18nKey, line.params)}</span>
                <span
                  className={
                    line.signedAmount < 0 ? "ledger__amount ledger__amount--out" : "ledger__amount"
                  }
                >
                  <Crystals>{formatCrystalsSigned(locale, line.signedAmount, kind)}</Crystals>
                </span>
              </div>
              <time className="ledger__time" dateTime={line.createdAt}>
                {formatDateTime(locale, new Date(line.createdAt), timeZone)}
              </time>
              {onReportEntry && (
                <button
                  type="button"
                  className="ledger__report button"
                  onClick={() => onReportEntry(entry.id)}
                >
                  {t("support:report.from_ledger")}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {hasMore && onLoadMore && (
        <button type="button" className="button ledger__more" disabled={loading} onClick={onLoadMore}>
          {loading ? t("ledger.loading") : t("ledger.load_more")}
        </button>
      )}
    </section>
  );
}
